const toArray = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeText = (value = "") =>
  String(value || "").trim().toLowerCase();

export const MANUFACTURING_JOB_STATUSES = [
  "Draft",
  "Planning",
  "Ready",
  "Waiting for Material",
  "Waiting",
  "In Production",
  "Inspection",
  "Blocked",
  "Complete",
  "Archived",
];

export const normalizeManufacturingStatus = (status = "") => {
  const normalized = normalizeText(status);
  if (!normalized) return "Draft";
  const directMatch = MANUFACTURING_JOB_STATUSES.find(
    (candidate) => normalizeText(candidate) === normalized
  );
  if (directMatch) return directMatch;

  const aliases = {
    active: "Planning",
    open: "Planning",
    started: "In Production",
    "in progress": "In Production",
    running: "In Production",
    held: "Blocked",
    hold: "Blocked",
    delayed: "Blocked",
    done: "Complete",
    completed: "Complete",
    closed: "Complete",
  };

  return aliases[normalized] || String(status || "Draft");
};

export const normalizeBomItem = (item = {}, index = 0) => {
  const quantity = Math.max(0, toNumber(item.quantity, 0));
  const unitCost = Math.max(0, toNumber(item.unitCost, 0));
  return {
    id: item.id || item.bomItemId || `bom-${index + 1}`,
    partNumber: item.partNumber || item.itemNumber || "",
    description: item.description || item.name || "BOM item",
    quantity,
    unit: item.unit || "each",
    material: item.material || item.materialName || "",
    itemType: item.itemType || item.type || "Raw material",
    vendor: item.vendor || "",
    vendorPartNumber: item.vendorPartNumber || "",
    unitCost,
    extendedCost: quantity * unitCost,
    requiredDate: item.requiredDate || item.neededDate || "",
    status: item.status || "Needed",
    notes: item.notes || "",
    revision: item.revision || "",
    source: item.source || "",
    attachmentId: item.attachmentId || item.fileId || "",
  };
};

export const getPlanBomItems = (job = {}) => {
  const explicitBom = toArray(job.bom || job.bomItems);
  if (explicitBom.length) return explicitBom.map(normalizeBomItem);

  return toArray(job.parts).map((part, index) =>
    normalizeBomItem(
      {
        id: `part-material-${part.id || index + 1}`,
        partNumber: part.partNumber || job.partNumber || "",
        description: part.name || job.name || "Primary material",
        quantity: Math.max(1, toNumber(part.quantity, job.quantity || 1)),
        unit: "piece",
        material: part.materialName || part.materialId || job.material || "",
        itemType: "Raw material",
        status: part.materialReady === true ? "Ready" : "Needed",
        notes: part.notes || "",
      },
      index
    )
  );
};

export const getPlanMaterialRequirements = (job = {}) =>
  getPlanBomItems(job).map((item) => ({
    id: item.id,
    material: item.material || item.description,
    quantity: item.quantity,
    unit: item.unit,
    neededDate: item.requiredDate || job.dueDate || "",
    projectId: job.projectId || job.id || "",
    jobId: job.id || "",
    jobName: job.name || "Manufacturing Plan",
    status: item.status || "Needed",
  }));

export const getOperationStatus = (operation = {}) =>
  normalizeText(operation.status || operation.completionStatus);

export const isOperationComplete = (operation = {}) =>
  ["complete", "completed", "done", "closed"].includes(
    getOperationStatus(operation)
  );

export const isOperationBlocked = (operation = {}) =>
  ["blocked", "hold", "waiting"].includes(getOperationStatus(operation));

export const calculateOperationProgress = (operations = []) => {
  const normalized = toArray(operations);
  const total = normalized.length;
  const completed = normalized.filter(isOperationComplete).length;
  return {
    completed,
    total,
    percent: total ? Math.round((completed / total) * 100) : 0,
  };
};

export const getCurrentOperation = (operations = []) => {
  const normalized = toArray(operations);
  return (
    normalized.find((operation) => isOperationBlocked(operation)) ||
    normalized.find((operation) => !isOperationComplete(operation)) ||
    normalized[normalized.length - 1] ||
    null
  );
};

export const getNextOperation = (operations = []) => {
  const normalized = toArray(operations);
  const current = getCurrentOperation(normalized);
  if (!current) return null;
  const index = normalized.findIndex((operation) => operation.id === current.id);
  return (
    normalized
      .slice(index + 1)
      .find((operation) => !isOperationComplete(operation)) || null
  );
};

export const calculateManufacturingReadiness = (job = {}) => {
  const operations = toArray(job.operations);
  const bomItems = getPlanBomItems(job);
  const hasMachine =
    toArray(job.machineRoute).length > 0 ||
    operations.some((operation) => operation.machineId || operation.machine);
  const hasTools = operations.some(
    (operation) =>
      operation.toolsUsed ||
      operation.toolDescription ||
      toArray(operation.toolIds).length > 0
  );
  const hasFiles =
    toArray(job.files).length > 0 ||
    toArray(job.parts).some((part) => toArray(part.files).length > 0);
  const materialReady =
    bomItems.length > 0 &&
    bomItems.every((item) =>
      ["ready", "available", "ordered", "received", "complete"].includes(
        normalizeText(item.status)
      )
    );

  return {
    bomReady: bomItems.length > 0,
    materialReady,
    routingReady: operations.length > 0,
    machineAssigned: hasMachine,
    toolsReady: hasTools,
    filesReady: hasFiles,
    readyForProduction:
      operations.length > 0 && hasMachine && hasTools && materialReady,
  };
};

export const deriveManufacturingPlanState = (job = {}, today = new Date()) => {
  const status = normalizeManufacturingStatus(job.status);
  const operations = toArray(job.operations);
  const progress = calculateOperationProgress(operations);
  const readiness = calculateManufacturingReadiness(job);
  const dueDate = job.dueDate || job.requiredDate || "";
  const dueTime = dueDate ? new Date(dueDate).getTime() : 0;
  const nowTime = new Date(today).setHours(0, 0, 0, 0);
  const daysUntilDue = dueTime
    ? Math.ceil((dueTime - nowTime) / (24 * 60 * 60 * 1000))
    : null;
  const blocked =
    status === "Blocked" ||
    operations.some(isOperationBlocked);

  return {
    status,
    progress,
    readiness,
    currentOperation: getCurrentOperation(operations),
    nextOperation: getNextOperation(operations),
    dueDate,
    daysUntilDue,
    isLate:
      status !== "Complete" &&
      status !== "Archived" &&
      daysUntilDue !== null &&
      daysUntilDue < 0,
    dueThisWeek:
      status !== "Complete" &&
      status !== "Archived" &&
      daysUntilDue !== null &&
      daysUntilDue >= 0 &&
      daysUntilDue <= 7,
    blocked,
  };
};

export const buildManufacturingOperationsDashboard = (
  jobs = [],
  today = new Date()
) => {
  const planStates = toArray(jobs).map((job) => ({
    job,
    ...deriveManufacturingPlanState(job, today),
  }));

  const countWhere = (predicate) => planStates.filter(predicate).length;
  const readinessCount = (key) =>
    countWhere((item) => item.readiness?.[key] === true);

  return {
    activeJobs: countWhere(
      (item) => !["Complete", "Archived"].includes(item.status)
    ),
    jobsDueThisWeek: countWhere((item) => item.dueThisWeek),
    lateJobs: countWhere((item) => item.isLate),
    readyForProduction: readinessCount("readyForProduction"),
    waitingForMaterial: countWhere(
      (item) =>
        item.status === "Waiting for Material" ||
        item.readiness.materialReady === false
    ),
    inProduction: countWhere((item) => item.status === "In Production"),
    completed: countWhere((item) => item.status === "Complete"),
    blocked: countWhere((item) => item.blocked && item.status !== "Complete"),
    bomComplete: readinessCount("bomReady"),
    routingComplete: readinessCount("routingReady"),
    machineAssigned: readinessCount("machineAssigned"),
    toolingReady: readinessCount("toolsReady"),
    planStates,
  };
};
